import React, { FC } from 'react';

import { Node } from 'src/common/interfaces/node.interface';
import { RadioGroup } from 'src/common/form/radio-group';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import objectPath from 'object-path';
import { apiUpdateStepNode } from 'src/utils/journeyApi';
import { Step } from 'src/common/interfaces/step.interface';
import { useEditorContext } from 'src/store/editor';
import { Popover } from 'src/common/ui/popover';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { LeftStrongIcon } from 'src/monet/icons';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { SmallPanel } from 'src/common/ui/small-panel';

const SECURITY_VALUE_ANYONE = 'anyone';
const SECURITY_VALUE_EMAIL_REQUIRED = 'email_required';

interface ContentSecurityPanelProps
  extends Pick<
    JourneyMenuPropsInterface,
    'renderOrigin' | 'openRef' | 'closeRef' | 'onOpen' | 'onClose' | 'offset' | 'placement'
  > {
  step: Step;
  node: Node;
  onBack?: () => void;
  open?: boolean;
}

export const ContentSecurityPanel: FC<ContentSecurityPanelProps> = ({
  step,
  node,
  renderOrigin,
  openRef,
  placement,
  closeRef,
  offset,
  onOpen,
  onClose,
  onBack,
}) => {
  const { updateNodeInJourney } = useEditorContext();
  const { setSuccessNotification } = useNotificationMessage();

  const formikParams = {
    initialValues: {
      security: objectPath.get(node, 'is_email_required', false)
        ? SECURITY_VALUE_EMAIL_REQUIRED
        : SECURITY_VALUE_ANYONE,
    },
    onSubmit: (values: any) => {
      const isEmailRequired = values.security === SECURITY_VALUE_EMAIL_REQUIRED;

      return apiUpdateStepNode(step.id, node.id, {
        is_email_required: isEmailRequired,
      }).then((nodeResponse) => {
        updateNodeInJourney(nodeResponse, step);
        onClose && onClose();
        const message = isEmailRequired ? 'Email capture is now enabled.' : 'Email capture is now disabled.';
        setSuccessNotification(message);
      });
    },
  };

  const hasValueChanged = (currentValue: string) => {
    return formikParams.initialValues.security !== currentValue;
  };

  return (
    <SmallPanel
      placement={placement}
      offset={offset}
      openRef={openRef}
      closeRef={closeRef}
      openAfterMount
      fixed
      hasBackButton
      onBack={onBack}
      onOpen={onOpen}
      onClose={onClose}
      renderOrigin={renderOrigin}
    >
      <div className='flex flex-col'>
        <div className='text-bedrock-p text-bedrock-black'>Enable email capture on individual Steps</div>
        <JourneyFormik
          {...formikParams}
          render={(formProps: any) => (
            <JourneyForm>
              <RadioGroup
                name='security'
                className='mt-md'
                options={[
                  {
                    label: 'Anyone can view',
                    value: SECURITY_VALUE_ANYONE,
                  },
                  {
                    label: 'Require email to view',
                    value: SECURITY_VALUE_EMAIL_REQUIRED,
                  },
                ]}
                value={formProps.values.security}
                onChange={(option) => {
                  console.log('on change value', option.value);
                  formProps.setFieldValue('security', option.value);
                }}
              />
              <button
                type='submit'
                className='mt-md btn-bedrock-primary'
                disabled={!hasValueChanged(formProps.values.security)}
              >
                Update
              </button>
            </JourneyForm>
          )}
        ></JourneyFormik>
      </div>
    </SmallPanel>
  );
};
